import { Injectable } from '@angular/core';
import {
  VIDEO_COMPRESSION_THRESHOLD_MB,
  VIDEO_MAX_SOURCE_MB,
  VIDEO_SERVER_UPLOAD_LIMIT_MB,
  VIDEO_UPLOAD_TARGET_MB,
} from './video-upload.constants';

interface CompressionProfile {
  maxWidth: number;
  maxHeight: number;
  bitrate: number;
  frameRate: number;
}

@Injectable({
  providedIn: 'root',
})
export class VideoCompressionService {
  private readonly UPLOAD_LIMIT = VIDEO_SERVER_UPLOAD_LIMIT_MB * 1024 * 1024;
  private readonly TARGET_SIZE = VIDEO_UPLOAD_TARGET_MB * 1024 * 1024;
  private readonly COMPRESSION_THRESHOLD = VIDEO_COMPRESSION_THRESHOLD_MB * 1024 * 1024;
  private readonly MAX_SOURCE_BYTES = VIDEO_MAX_SOURCE_MB * 1024 * 1024;

  private readonly PROFILES: CompressionProfile[] = [
    { maxWidth: 1280, maxHeight: 720, bitrate: 2_000_000, frameRate: 24 },
    { maxWidth: 960, maxHeight: 540, bitrate: 1_400_000, frameRate: 22 },
    { maxWidth: 854, maxHeight: 480, bitrate: 1_000_000, frameRate: 20 },
    { maxWidth: 640, maxHeight: 360, bitrate: 750_000, frameRate: 18 },
    { maxWidth: 426, maxHeight: 240, bitrate: 450_000, frameRate: 15 },
  ];

  async compressVideo(file: File, onProgress?: (progress: number) => void): Promise<File> {
    if (!this.needsCompression(file)) {
      onProgress?.(100);
      return file;
    }

    if (file.size > this.MAX_SOURCE_BYTES) {
      throw new Error(
        `Video is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum source size is ${VIDEO_MAX_SOURCE_MB}MB.`
      );
    }

    onProgress?.(2);
    const video = await this.loadVideo(file);
    let bestFile: File | null = null;

    try {
      for (let i = 0; i < this.PROFILES.length; i++) {
        const profile = this.PROFILES[i];
        const passStart = 5 + (i / this.PROFILES.length) * 90;
        const passEnd = 5 + ((i + 1) / this.PROFILES.length) * 90;

        const compressed = await this.compressWithProfile(
          file,
          video,
          profile,
          (passProgress) => {
            onProgress?.(passStart + (passProgress / 100) * (passEnd - passStart));
          }
        );

        if (!bestFile || compressed.size < bestFile.size) {
          bestFile = compressed;
        }

        if (compressed.size <= this.TARGET_SIZE) {
          onProgress?.(100);
          return compressed;
        }
      }

      if (bestFile && bestFile.size <= this.UPLOAD_LIMIT) {
        onProgress?.(100);
        return bestFile;
      }

      const resultMb = ((bestFile?.size ?? file.size) / 1024 / 1024).toFixed(1);
      throw new Error(
        `Video could not be optimized below ${VIDEO_SERVER_UPLOAD_LIMIT_MB}MB (best result: ${resultMb}MB). Try a shorter or lower-resolution recording.`
      );
    } finally {
      this.cleanupVideo(video);
      video.remove();
    }
  }

  isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  needsCompression(file: File): boolean {
    if (!this.isVideoFile(file) && !file.name.match(/\.(mp4|mov|webm|mkv|avi|m4v|wmv|flv|mpeg|mpg)$/i)) {
      return false;
    }
    return file.size > this.COMPRESSION_THRESHOLD;
  }

  private async loadVideo(file: File): Promise<HTMLVideoElement> {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = false;
    video.volume = 0;
    video.playsInline = true;

    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Video metadata loading timeout')), 60_000);
      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        video.currentTime = 0.01;
        resolve();
      };
      video.onerror = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(videoUrl);
        reject(new Error('Failed to load video metadata'));
      };
    });

    video.dataset['objectUrl'] = videoUrl;
    return video;
  }

  private cleanupVideo(video: HTMLVideoElement): void {
    const url = video.dataset['objectUrl'];
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  private async compressWithProfile(
    file: File,
    video: HTMLVideoElement,
    profile: CompressionProfile,
    onProgress?: (progress: number) => void
  ): Promise<File> {
    const { width, height } = this.getTargetDimensions(
      video.videoWidth,
      video.videoHeight,
      profile.maxWidth,
      profile.maxHeight
    );

    const duration = video.duration;
    if (!duration || duration === 0) {
      throw new Error('Invalid video duration');
    }

    const durationBitrate = Math.floor((this.TARGET_SIZE * 8 * 0.9) / duration);
    const targetBitrate = Math.min(
      profile.bitrate,
      Math.max(350_000, durationBitrate)
    );

    const compressedBlob = await this.compressWithMediaRecorder(
      video,
      width,
      height,
      targetBitrate,
      profile.frameRate,
      onProgress
    );

    const fileName = file.name.replace(/\.[^/.]+$/, '') + '_optimized.mp4';
    return new File(
      [compressedBlob],
      fileName,
      { type: compressedBlob.type || 'video/mp4', lastModified: Date.now() }
    );
  }

  private getTargetDimensions(
    videoWidth: number,
    videoHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let targetWidth = videoWidth;
    let targetHeight = videoHeight;

    if (videoWidth > maxWidth || videoHeight > maxHeight) {
      const aspectRatio = videoWidth / videoHeight;
      if (videoWidth >= videoHeight) {
        targetWidth = Math.min(maxWidth, videoWidth);
        targetHeight = targetWidth / aspectRatio;
      } else {
        targetHeight = Math.min(maxHeight, videoHeight);
        targetWidth = targetHeight * aspectRatio;
      }
    }

    return {
      width: Math.max(2, Math.floor(targetWidth / 2) * 2),
      height: Math.max(2, Math.floor(targetHeight / 2) * 2),
    };
  }

  private async compressWithMediaRecorder(
    video: HTMLVideoElement,
    width: number,
    height: number,
    bitrate: number,
    frameRate: number,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { willReadFrequently: false });

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      const canvasStream = canvas.captureStream(frameRate);
      const combinedStream = this.buildStreamWithAudio(canvasStream, video);
      const mimeType = this.getSupportedMimeType();
      const options: MediaRecorderOptions = {
        mimeType,
        videoBitsPerSecond: bitrate,
      };

      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        delete options.mimeType;
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      const chunks: Blob[] = [];
      let isRecording = false;
      let animationFrameId = 0;

      const stopRecording = () => {
        if (!isRecording) {
          return;
        }
        isRecording = false;
        mediaRecorder.stop();
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };

      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType }));
      };

      mediaRecorder.onerror = () => {
        stopRecording();
        reject(new Error('MediaRecorder error'));
      };

      try {
        mediaRecorder.start(100);
        isRecording = true;
      } catch (error) {
        reject(error);
        return;
      }

      const drawFrame = () => {
        if (!isRecording || video.ended) {
          stopRecording();
          return;
        }

        try {
          ctx.drawImage(video, 0, 0, width, height);
          if (onProgress && video.duration && !isNaN(video.duration)) {
            onProgress(Math.min((video.currentTime / video.duration) * 100, 99));
          }
          animationFrameId = requestAnimationFrame(drawFrame);
        } catch {
          stopRecording();
        }
      };

      video.currentTime = 0;
      video.onended = () => stopRecording();

      video.play().then(() => drawFrame()).catch((error) => {
        stopRecording();
        reject(error);
      });
    });
  }

  private buildStreamWithAudio(canvasStream: MediaStream, video: HTMLVideoElement): MediaStream {
    const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];

    try {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaElementSource(video);
      const destination = audioContext.createMediaStreamDestination();
      source.connect(destination);
      const audioTracks = destination.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        tracks.push(...audioTracks);
        return new MediaStream(tracks);
      }
    } catch {
      // Fall back to captureStream below.
    }

    const videoWithCapture = video as HTMLVideoElement & {
      captureStream?: () => MediaStream;
      mozCaptureStream?: () => MediaStream;
    };
    const captureFromVideo =
      videoWithCapture.captureStream?.() ?? videoWithCapture.mozCaptureStream?.();
    tracks.push(...(captureFromVideo?.getAudioTracks() ?? []));
    return new MediaStream(tracks);
  }

  private getSupportedMimeType(): string {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4;codecs=avc1',
      'video/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm';
  }
}
